export type PickerSectionId = 'favorites' | 'start' | 'website' | 'independent' | 'deployed';

const PICKER_SECTION_STORAGE_KEY = 'designstudio.pickerSectionId';

/** Rail order: favorites first when present, then start (quick prompt). */
const PICKER_SECTION_IDS: readonly PickerSectionId[] = [
  'favorites',
  'start',
  'website',
  'independent',
  'deployed',
];

export type ResolvePickerSectionInput = {
  readonly hasFavorites: boolean;
  readonly websiteCount: number;
  readonly independentCount: number;
  readonly canCreateScratchApp: boolean;
  readonly deploymentCount: number;
  readonly stored?: PickerSectionId | undefined;
};

export function readStoredPickerSectionId(): PickerSectionId | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const value = window.sessionStorage.getItem(PICKER_SECTION_STORAGE_KEY);
  if (value === null || value === '') {
    return undefined;
  }
  return parsePickerSectionId(value);
}

export function writeStoredPickerSectionId(section: PickerSectionId): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.setItem(PICKER_SECTION_STORAGE_KEY, section);
}

export function parsePickerSectionId(value: string): PickerSectionId | undefined {
  return PICKER_SECTION_IDS.find((id) => id === value);
}

export function isPickerSectionAvailable(
  section: PickerSectionId,
  input: Pick<ResolvePickerSectionInput, 'hasFavorites'>
): boolean {
  if (section === 'favorites') {
    return input.hasFavorites;
  }
  return true;
}

/**
 * After login / picker mount: land on favorites when any exist.
 * Otherwise the start (quick-prompt) page — not website.
 * Stored tab is ignored so login always prefers favorites / start.
 */
export function resolveInitialPickerSection(input: ResolvePickerSectionInput): PickerSectionId {
  if (input.hasFavorites) {
    return 'favorites';
  }
  return 'start';
}

export function clampPickerSectionIndex(index: number, length: number): number {
  if (length <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(length - 1, index));
}

export function pickerSectionIndex(
  sections: readonly PickerSectionId[],
  section: PickerSectionId
): number {
  const index = sections.indexOf(section);
  return index === -1 ? 0 : index;
}

export function pickerSectionAtIndex(
  sections: readonly PickerSectionId[],
  index: number
): PickerSectionId | undefined {
  if (sections.length === 0) {
    return undefined;
  }
  return sections[clampPickerSectionIndex(index, sections.length)];
}
